import _defineProperty from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { TRACING_DEFAULTS, getLocationHref, browserPerformanceTimeOrigin, parseStringToURLObject, registerSpanErrorInstrumentation, GLOBAL_OBJ, getClient, debug, getIsolationScope, generateTraceId, getCurrentScope, propagationContextFromHeaders, spanToJSON, dateTimestampInSeconds, timestampInSeconds, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, startInactiveSpan, startIdleSpan, getDynamicSamplingContextFromSpan, spanIsSampled, SEMANTIC_ATTRIBUTE_SENTRY_IDLE_SPAN_FINISH_REASON, addNonEnumerableProperty, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN } from '@sentry/core';
import { addHistoryInstrumentationHandler, registerInpInteractionListener, startTrackingWebVitals, startTrackingINP, startTrackingElementTiming, startTrackingLongAnimationFrames, startTrackingLongTasks, startTrackingInteractions, addPerformanceEntries } from '@sentry-internal/browser-utils';
import { DEBUG_BUILD } from '../debug-build.js';
import { WINDOW, getHttpRequestData } from '../helpers.js';
import { registerBackgroundTabDetection } from './backgroundtab.js';
import { linkTraces } from './linkedTraces.js';
import { defaultRequestInstrumentationOptions, instrumentOutgoingRequests } from './request.js';
var BROWSER_TRACING_INTEGRATION_ID = 'BrowserTracing';
var DEFAULT_BROWSER_TRACING_OPTIONS = _objectSpread(_objectSpread({}, TRACING_DEFAULTS), {}, {
  instrumentNavigation: true,
  instrumentPageLoad: true,
  markBackgroundSpan: true,
  enableLongTask: true,
  enableLongAnimationFrame: true,
  enableInp: true,
  enableElementTiming: true,
  ignoreResourceSpans: [],
  ignorePerformanceApiSpans: [],
  detectRedirects: true,
  linkPreviousTrace: 'in-memory',
  consistentTraceSampling: false,
  _experiments: {}
}, defaultRequestInstrumentationOptions);

/**
 * The Browser Tracing integration automatically instruments browser pageload/navigation
 * actions as transactions, and captures requests, metrics and errors as spans.
 *
 * The integration can be configured with a variety of options, and can be extended to use
 * any routing library.
 *
 * We explicitly export the proper type here, as this has to be extended in some cases.
 */
var browserTracingIntegration = function browserTracingIntegration() {
  var _options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var latestRoute = {
    name: undefined,
    source: undefined
  };

  /**
   * This is just a small wrapper that makes `document` optional.
   * We want to be extra-safe and always check that this exists, to ensure weird environments do not blow up.
   */
  var optionalWindowDocument = WINDOW.document;
  var _DEFAULT_BROWSER_TRAC = _objectSpread(_objectSpread({}, DEFAULT_BROWSER_TRACING_OPTIONS), _options),
    enableInp = _DEFAULT_BROWSER_TRAC.enableInp,
    enableElementTiming = _DEFAULT_BROWSER_TRAC.enableElementTiming,
    enableLongTask = _DEFAULT_BROWSER_TRAC.enableLongTask,
    enableLongAnimationFrame = _DEFAULT_BROWSER_TRAC.enableLongAnimationFrame,
    _DEFAULT_BROWSER_TRAC2 = _DEFAULT_BROWSER_TRAC._experiments,
    enableInteractions = _DEFAULT_BROWSER_TRAC2.enableInteractions,
    enableStandaloneClsSpans = _DEFAULT_BROWSER_TRAC2.enableStandaloneClsSpans,
    enableStandaloneLcpSpans = _DEFAULT_BROWSER_TRAC2.enableStandaloneLcpSpans,
    beforeStartSpan = _DEFAULT_BROWSER_TRAC.beforeStartSpan,
    idleTimeout = _DEFAULT_BROWSER_TRAC.idleTimeout,
    finalTimeout = _DEFAULT_BROWSER_TRAC.finalTimeout,
    childSpanTimeout = _DEFAULT_BROWSER_TRAC.childSpanTimeout,
    markBackgroundSpan = _DEFAULT_BROWSER_TRAC.markBackgroundSpan,
    traceFetch = _DEFAULT_BROWSER_TRAC.traceFetch,
    traceXHR = _DEFAULT_BROWSER_TRAC.traceXHR,
    trackFetchStreamPerformance = _DEFAULT_BROWSER_TRAC.trackFetchStreamPerformance,
    shouldCreateSpanForRequest = _DEFAULT_BROWSER_TRAC.shouldCreateSpanForRequest,
    enableHTTPTimings = _DEFAULT_BROWSER_TRAC.enableHTTPTimings,
    ignoreResourceSpans = _DEFAULT_BROWSER_TRAC.ignoreResourceSpans,
    ignorePerformanceApiSpans = _DEFAULT_BROWSER_TRAC.ignorePerformanceApiSpans,
    instrumentPageLoad = _DEFAULT_BROWSER_TRAC.instrumentPageLoad,
    instrumentNavigation = _DEFAULT_BROWSER_TRAC.instrumentNavigation,
    detectRedirects = _DEFAULT_BROWSER_TRAC.detectRedirects,
    linkPreviousTrace = _DEFAULT_BROWSER_TRAC.linkPreviousTrace,
    consistentTraceSampling = _DEFAULT_BROWSER_TRAC.consistentTraceSampling,
    onRequestSpanStart = _DEFAULT_BROWSER_TRAC.onRequestSpanStart;
  var _collectWebVitals;
  var lastInteractionTimestamp;

  /** Create routing idle transaction. */
  function _createRouteSpan(client, startSpanOptions) {
    var makeActive = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var isPageloadTransaction = startSpanOptions.op === 'pageload';
    var finalStartSpanOptions = beforeStartSpan ? beforeStartSpan(startSpanOptions) : startSpanOptions;
    var attributes = finalStartSpanOptions.attributes || {};

    // If `finalStartSpanOptions.name` is different than `startSpanOptions.name`
    // it is because `beforeStartSpan` set a custom name. Therefore we set the source to 'custom'.
    if (startSpanOptions.name !== finalStartSpanOptions.name) {
      attributes[SEMANTIC_ATTRIBUTE_SENTRY_SOURCE] = 'custom';
      finalStartSpanOptions.attributes = attributes;
    }
    if (!makeActive) {
      // We want to ensure this has 0s duration
      var now = dateTimestampInSeconds();
      startInactiveSpan(_objectSpread(_objectSpread({}, finalStartSpanOptions), {}, {
        startTime: now
      })).end(now);
      return;
    }
    latestRoute.name = finalStartSpanOptions.name;
    latestRoute.source = attributes[SEMANTIC_ATTRIBUTE_SENTRY_SOURCE];
    var idleSpan = startIdleSpan(finalStartSpanOptions, {
      idleTimeout: idleTimeout,
      finalTimeout: finalTimeout,
      childSpanTimeout: childSpanTimeout,
      // should wait for finish signal if it's a pageload transaction
      disableAutoFinish: isPageloadTransaction,
      beforeSpanEnd: function beforeSpanEnd(span) {
        var _collectWebVitals2;
        // This will generally always be defined here, because it is set in `setup()` of the integration
        // but technically, it is optional, so we guard here to be extra safe
        (_collectWebVitals2 = _collectWebVitals) === null || _collectWebVitals2 === void 0 || _collectWebVitals2();
        addPerformanceEntries(span, {
          recordClsOnPageloadSpan: !enableStandaloneClsSpans,
          recordLcpOnPageloadSpan: !enableStandaloneLcpSpans,
          ignoreResourceSpans: ignoreResourceSpans,
          ignorePerformanceApiSpans: ignorePerformanceApiSpans
        });
        setActiveIdleSpan(client, undefined);

        // A trace should stay consistent over the entire timespan of one route - even after the pageload/navigation ended.
        // Only when another navigation happens, we want to create a new trace.
        // This way, e.g. errors that occur after the pageload span ended are still associated to the pageload trace.
        var scope = getCurrentScope();
        var oldPropagationContext = scope.getPropagationContext();
        scope.setPropagationContext(_objectSpread(_objectSpread({}, oldPropagationContext), {}, {
          traceId: idleSpan.spanContext().traceId,
          sampled: spanIsSampled(idleSpan),
          dsc: getDynamicSamplingContextFromSpan(span)
        }));
      }
    });
    setActiveIdleSpan(client, idleSpan);
    function emitFinish() {
      if (optionalWindowDocument && ['interactive', 'complete'].includes(optionalWindowDocument.readyState)) {
        client.emit('idleSpanEnableAutoFinish', idleSpan);
      }
    }
    if (isPageloadTransaction && optionalWindowDocument) {
      optionalWindowDocument.addEventListener('readystatechange', function () {
        emitFinish();
      });
      emitFinish();
    }
  }
  return {
    name: BROWSER_TRACING_INTEGRATION_ID,
    setup: function setup(client) {
      registerSpanErrorInstrumentation();
      _collectWebVitals = startTrackingWebVitals({
        recordClsStandaloneSpans: enableStandaloneClsSpans || false,
        recordLcpStandaloneSpans: enableStandaloneLcpSpans || false,
        client: client
      });
      if (enableInp) {
        startTrackingINP();
      }
      if (enableElementTiming) {
        startTrackingElementTiming();
      }
      if (enableLongAnimationFrame && GLOBAL_OBJ.PerformanceObserver && PerformanceObserver.supportedEntryTypes && PerformanceObserver.supportedEntryTypes.includes('long-animation-frame')) {
        startTrackingLongAnimationFrames();
      } else if (enableLongTask) {
        startTrackingLongTasks();
      }
      if (enableInteractions) {
        startTrackingInteractions();
      }
      if (detectRedirects && optionalWindowDocument) {
        var interactionHandler = function interactionHandler() {
          lastInteractionTimestamp = timestampInSeconds();
        };
        addEventListener('click', interactionHandler, {
          capture: true
        });
        addEventListener('keydown', interactionHandler, {
          capture: true,
          passive: true
        });
      }
      function maybeEndActiveSpan() {
        var activeSpan = getActiveIdleSpan(client);
        if (activeSpan && !spanToJSON(activeSpan).timestamp) {
          DEBUG_BUILD && debug.log("[Tracing] Finishing current active span with op: ".concat(spanToJSON(activeSpan).op));
          // If there's an open active span, we need to finish it before creating an new one.
          activeSpan.setAttribute(SEMANTIC_ATTRIBUTE_SENTRY_IDLE_SPAN_FINISH_REASON, 'cancelled');
          activeSpan.end();
        }
      }
      client.on('startNavigationSpan', function (startSpanOptions, navigationOptions) {
        if (getClient() !== client) {
          return;
        }
        if (navigationOptions !== null && navigationOptions !== void 0 && navigationOptions.isRedirect) {
          DEBUG_BUILD && debug.warn('[Tracing] Detected redirect, navigation span will not be the root span, but a child span.');
          _createRouteSpan(client, _objectSpread({
            op: 'navigation.redirect'
          }, startSpanOptions), false);
          return;
        }
        maybeEndActiveSpan();
        getIsolationScope().setPropagationContext({
          traceId: generateTraceId(),
          sampleRand: Math.random()
        });
        var scope = getCurrentScope();
        scope.setPropagationContext({
          traceId: generateTraceId(),
          sampleRand: Math.random()
        });
        // We reset this to ensure we do not have lingering incorrect data here
        // places that call this hook may set this where appropriate - else, the URL at span sending time is used
        scope.setSDKProcessingMetadata({
          normalizedRequest: undefined
        });
        _createRouteSpan(client, _objectSpread({
          op: 'navigation'
        }, startSpanOptions));
      });
      client.on('startPageLoadSpan', function (startSpanOptions) {
        var traceOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        if (getClient() !== client) {
          return;
        }
        maybeEndActiveSpan();
        var sentryTrace = traceOptions.sentryTrace || getMetaContent('sentry-trace');
        var baggage = traceOptions.baggage || getMetaContent('baggage');
        var propagationContext = propagationContextFromHeaders(sentryTrace, baggage);
        var scope = getCurrentScope();
        scope.setPropagationContext(propagationContext);

        // We store the normalized request data on the scope, so we get the request data at time of span creation
        // otherwise, the URL etc. may already be of the following navigation, and we'd report the wrong URL
        scope.setSDKProcessingMetadata({
          normalizedRequest: getHttpRequestData()
        });
        _createRouteSpan(client, _objectSpread({
          op: 'pageload'
        }, startSpanOptions));
      });
    },
    afterAllSetup: function afterAllSetup(client) {
      var startingUrl = getLocationHref();
      if (linkPreviousTrace !== 'off') {
        linkTraces(client, {
          linkPreviousTrace: linkPreviousTrace,
          consistentTraceSampling: consistentTraceSampling
        });
      }
      if (WINDOW.location) {
        if (instrumentPageLoad) {
          var origin = browserPerformanceTimeOrigin();
          startBrowserTracingPageLoadSpan(client, {
            name: WINDOW.location.pathname,
            // pageload should always start at timeOrigin (and needs to be in s, not ms)
            startTime: origin ? origin / 1000 : undefined,
            attributes: _defineProperty(_defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'url'), SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, 'auto.pageload.browser')
          });
        }
        if (instrumentNavigation) {
          addHistoryInstrumentationHandler(function (_ref) {
            var _startingUrl;
            var to = _ref.to,
              from = _ref.from;
            /**
             * This early return is there to account for some cases where a navigation transaction starts right after
             * long-running pageload. We make sure that if `from` is undefined and a valid `startingURL` exists, we don't
             * create an uneccessary navigation transaction.
             *
             * This was hard to duplicate, but this behavior stopped as soon as this fix was applied. This issue might also
             * only be caused in certain development environments where the usage of a hot module reloader is causing
             * errors.
             */
            if (from === undefined && ((_startingUrl = startingUrl) === null || _startingUrl === void 0 ? void 0 : _startingUrl.indexOf(to)) !== -1) {
              startingUrl = undefined;
              return;
            }
            startingUrl = undefined;
            var parsed = parseStringToURLObject(to);
            var activeSpan = getActiveIdleSpan(client);
            var navigationIsRedirect = activeSpan && detectRedirects && isRedirect(activeSpan, lastInteractionTimestamp);
            startBrowserTracingNavigationSpan(client, {
              name: (parsed === null || parsed === void 0 ? void 0 : parsed.pathname) || WINDOW.location.pathname,
              attributes: _defineProperty(_defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'url'), SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, 'auto.navigation.browser')
            }, {
              url: to,
              isRedirect: navigationIsRedirect
            });
          });
        }
      }
      if (markBackgroundSpan) {
        registerBackgroundTabDetection();
      }
      if (enableInteractions) {
        registerInteractionListener(client, idleTimeout, finalTimeout, childSpanTimeout, latestRoute);
      }
      if (enableInp) {
        registerInpInteractionListener();
      }
      instrumentOutgoingRequests(client, {
        traceFetch: traceFetch,
        traceXHR: traceXHR,
        trackFetchStreamPerformance: trackFetchStreamPerformance,
        tracePropagationTargets: client.getOptions().tracePropagationTargets,
        shouldCreateSpanForRequest: shouldCreateSpanForRequest,
        enableHTTPTimings: enableHTTPTimings,
        onRequestSpanStart: onRequestSpanStart
      });
    }
  };
};

/**
 * Manually start a page load span.
 * This will only do something if a browser tracing integration integration has been setup.
 *
 * If you provide a custom `traceOptions` object, it will be used to continue the trace
 * instead of the default behavior, which is to look it up on the <meta> tags.
 */
function startBrowserTracingPageLoadSpan(client, spanOptions, traceOptions) {
  client.emit('startPageLoadSpan', spanOptions, traceOptions);
  getCurrentScope().setTransactionName(spanOptions.name);
  var pageloadSpan = getActiveIdleSpan(client);
  if (pageloadSpan) {
    client.emit('afterStartPageLoadSpan', pageloadSpan);
  }
  return pageloadSpan;
}

/**
 * Manually start a navigation span.
 * This will only do something if a browser tracing integration has been setup.
 */
function startBrowserTracingNavigationSpan(client, spanOptions, options) {
  var _ref2 = options || {},
    url = _ref2.url,
    isRedirect = _ref2.isRedirect;
  client.emit('beforeStartNavigationSpan', spanOptions, {
    isRedirect: isRedirect
  });
  client.emit('startNavigationSpan', spanOptions, {
    isRedirect: isRedirect
  });
  var scope = getCurrentScope();
  scope.setTransactionName(spanOptions.name);

  // We store the normalized request data on the scope, so we get the request data at time of span creation
  // otherwise, the URL etc. may already be of the following navigation, and we'd report the wrong URL
  if (url && !isRedirect) {
    scope.setSDKProcessingMetadata({
      normalizedRequest: _objectSpread(_objectSpread({}, getHttpRequestData()), {}, {
        url: url
      })
    });
  }
  return getActiveIdleSpan(client);
}

/** Returns the value of a meta tag */
function getMetaContent(metaName) {
  /**
   * This is just a small wrapper that makes `document` optional.
   * We want to be extra-safe and always check that this exists, to ensure weird environments do not blow up.
   */
  var optionalWindowDocument = WINDOW.document;
  var metaTag = optionalWindowDocument === null || optionalWindowDocument === void 0 ? void 0 : optionalWindowDocument.querySelector("meta[name=".concat(metaName, "]"));
  return (metaTag === null || metaTag === void 0 ? void 0 : metaTag.getAttribute('content')) || undefined;
}

/** Start listener for interaction transactions */
function registerInteractionListener(client, idleTimeout, finalTimeout, childSpanTimeout, latestRoute) {
  /**
   * This is just a small wrapper that makes `document` optional.
   * We want to be extra-safe and always check that this exists, to ensure weird environments do not blow up.
   */
  var optionalWindowDocument = WINDOW.document;
  var inflightInteractionSpan;
  var registerInteractionTransaction = function registerInteractionTransaction() {
    var op = 'ui.action.click';
    var activeIdleSpan = getActiveIdleSpan(client);
    if (activeIdleSpan) {
      var currentRootSpanOp = spanToJSON(activeIdleSpan).op;
      if (['navigation', 'pageload'].includes(currentRootSpanOp)) {
        DEBUG_BUILD && debug.warn("[Tracing] Did not create ".concat(op, " span because a pageload or navigation span is in progress."));
        return undefined;
      }
    }
    if (inflightInteractionSpan) {
      inflightInteractionSpan.setAttribute(SEMANTIC_ATTRIBUTE_SENTRY_IDLE_SPAN_FINISH_REASON, 'interactionInterrupted');
      inflightInteractionSpan.end();
      inflightInteractionSpan = undefined;
    }
    if (!latestRoute.name) {
      DEBUG_BUILD && debug.warn("[Tracing] Did not create ".concat(op, " transaction because _latestRouteName is missing."));
      return undefined;
    }
    inflightInteractionSpan = startIdleSpan({
      name: latestRoute.name,
      op: op,
      attributes: _defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, latestRoute.source || 'url')
    }, {
      idleTimeout: idleTimeout,
      finalTimeout: finalTimeout,
      childSpanTimeout: childSpanTimeout
    });
  };
  if (optionalWindowDocument) {
    addEventListener('click', registerInteractionTransaction, {
      capture: true
    });
  }
}

// We store the active idle span on the client object, so we can access it from exported functions
var ACTIVE_IDLE_SPAN_PROPERTY = '_sentry_idleSpan';
function getActiveIdleSpan(client) {
  return client[ACTIVE_IDLE_SPAN_PROPERTY];
}
function setActiveIdleSpan(client, span) {
  addNonEnumerableProperty(client, ACTIVE_IDLE_SPAN_PROPERTY, span);
}

// The max. time in seconds between two pageload/navigation spans that makes us consider the second one a redirect
var REDIRECT_THRESHOLD = 0.3;
function isRedirect(activeSpan, lastInteractionTimestamp) {
  var spanData = spanToJSON(activeSpan);
  var now = dateTimestampInSeconds();

  // More than 300ms since last navigation/pageload span?
  // --> never consider this a redirect
  var startTimestamp = spanData.start_timestamp;
  if (now - startTimestamp > REDIRECT_THRESHOLD) {
    return false;
  }

  // A click happened in the last 300ms?
  // --> never consider this a redirect
  if (lastInteractionTimestamp && now - lastInteractionTimestamp <= REDIRECT_THRESHOLD) {
    return false;
  }
  return true;
}
export { BROWSER_TRACING_INTEGRATION_ID, browserTracingIntegration, getMetaContent, startBrowserTracingNavigationSpan, startBrowserTracingPageLoadSpan };